import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Radio,
  Select,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconAlertCircle,
  IconCalendarStats,
  IconDownload,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import { ReportSectionHeader } from "../ReportSectionHeader";
import { getContractsByAddressSystem } from "../../../services/ContractService";
import { generateSiteMeetingReport } from "../../../services/SiteMeetingReportService";
import { ContractListItem } from "../../../types/UserRole";
import {
  SiteMeetingReportResponse,
  SiteMeetingObjectItem,
} from "../../../types/SiteMeetingReport";

interface SiteMeetingReportProps {
  onBack: () => void;
  showHeader?: boolean;
}

interface GeneratedReport {
  blob: Blob;
  filename: string;
}

const DATE_FORMAT = "DD.MM.YYYY";
const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export default function SiteMeetingReport({
  onBack,
  showHeader = true,
}: Readonly<SiteMeetingReportProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const { getTranslation } = useTranslation({
    pageId: 514,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();

  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsError, setContractsError] = useState<string | null>(null);
  const [contractStatus, setContractStatus] = useState<"active" | "archived">(
    "active",
  );
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    null,
  );
  const [previousMeetingDate, setPreviousMeetingDate] = useState<Date | null>(
    dayjs().subtract(30, "day").toDate(),
  );
  const [currentMeetingDate, setCurrentMeetingDate] = useState<Date | null>(
    new Date(),
  );
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generatedReport, setGeneratedReport] =
    useState<GeneratedReport | null>(null);

  useEffect(() => {
    if (!selectedAddressSystemId) {
      setContracts([]);
      setSelectedContractId(null);
      return;
    }

    const loadContracts = async () => {
      setContractsLoading(true);
      setContractsError(null);
      try {
        const items = await getContractsByAddressSystem(
          selectedAddressSystemId,
        );
        setContracts(items);
        if (items.length > 0) {
          setSelectedContractId(String(items[0].id));
        }
      } catch (error) {
        console.error("Failed to load contracts", error);
        setContractsError(
          getTranslation("REPORTS_CONTRACTS_LOAD_ERROR") ||
            "Unable to load contracts for the address system.",
        );
      } finally {
        setContractsLoading(false);
      }
    };

    loadContracts();
  }, [selectedAddressSystemId]);

  // Filter contracts based on contract status
  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      if (contractStatus === "active") {
        return contract.contractStatusDefaultValue === 1;
      } else {
        return contract.contractStatusDefaultValue !== 1;
      }
    });
  }, [contracts, contractStatus]);

  // Reset selected contract when status filter changes if it's not in filtered list
  useEffect(() => {
    if (selectedContractId) {
      const idNum = Number(selectedContractId);
      const isInFiltered = filteredContracts.some(
        (contract) => contract.id === idNum,
      );
      if (!isInFiltered && filteredContracts.length > 0) {
        setSelectedContractId(String(filteredContracts[0].id));
      } else if (!isInFiltered) {
        setSelectedContractId(null);
      }
    } else if (filteredContracts.length > 0) {
      setSelectedContractId(String(filteredContracts[0].id));
    }
  }, [contractStatus, filteredContracts, selectedContractId]);

  const selectedContract = useMemo(() => {
    if (!selectedContractId) return undefined;
    const idNum = Number(selectedContractId);
    return filteredContracts.find((contract) => contract.id === idNum);
  }, [filteredContracts, selectedContractId]);

  const formatDate = (value: Date) => dayjs(value).format(DATE_FORMAT);

  const sanitizeFileSegment = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const buildWorkbook = (
    contractName: string,
    prevDate: string,
    currDate: string,
    approvedObjects: SiteMeetingObjectItem[],
    approvedHeldObjects: SiteMeetingObjectItem[],
  ) => {
    const workbook = XLSX.utils.book_new();
    
    // Header section
    const sheetData: any[][] = [
      [getTranslation("REPORTS_SHEET_TITLE") || "Site meeting report"],
      [],
      [getTranslation("REPORTS_SHEET_CONTRACT") || "Contract", contractName],
      [
        getTranslation("REPORTS_SHEET_PREV_MEETING") ||
          "Date of the previous meeting",
        prevDate,
      ],
      [
        getTranslation("REPORTS_SHEET_CURR_MEETING") ||
          "Date of this meeting",
        currDate,
      ],
      [],
    ];

    // Section 7.1: Approved objects (Hyväksytty)
    sheetData.push([
      getTranslation("REPORTS_SECTION_71_TITLE") || "7.1 Approved objects (Hyväksytty)",
    ]);
    sheetData.push([]);

    if (approvedObjects.length > 0) {
      sheetData.push([
        getTranslation("REPORTS_COLUMN_LOCATION") || "Location",
        getTranslation("REPORTS_COLUMN_DESCRIPTION") || "Description",
        getTranslation("REPORTS_COLUMN_PLANNED_DATE") || "Planned Date",
        getTranslation("REPORTS_COLUMN_TYPE") || "Type",
        getTranslation("REPORTS_COLUMN_STATUS") || "Status",
      ]);

      approvedObjects.forEach((obj) => {
        sheetData.push([
          obj.location || "",
          obj.description || "",
          obj.plannedDate ? dayjs(obj.plannedDate).format(DATE_FORMAT) : "",
          obj.typeLabel || "",
          obj.statusLabel || "",
        ]);
      });
    } else {
      sheetData.push([
        getTranslation("REPORTS_NO_OBJECTS") || "No objects in this category.",
      ]);
    }

    sheetData.push([]);

    // Section 7.2: Approved and held objects (Hyväksytty ja pidätetty)
    sheetData.push([
      getTranslation("REPORTS_SECTION_72_TITLE") ||
        "7.2 Approved and held objects (Hyväksytty ja pidätetty)",
    ]);
    sheetData.push([]);

    if (approvedHeldObjects.length > 0) {
      sheetData.push([
        getTranslation("REPORTS_COLUMN_LOCATION") || "Location",
        getTranslation("REPORTS_COLUMN_DESCRIPTION") || "Description",
        getTranslation("REPORTS_COLUMN_PLANNED_DATE") || "Planned Date",
        getTranslation("REPORTS_COLUMN_TYPE") || "Type",
        getTranslation("REPORTS_COLUMN_STATUS") || "Status",
      ]);

      approvedHeldObjects.forEach((obj) => {
        sheetData.push([
          obj.location || "",
          obj.description || "",
          obj.plannedDate ? dayjs(obj.plannedDate).format(DATE_FORMAT) : "",
          obj.typeLabel || "",
          obj.statusLabel || "",
        ]);
      });
    } else {
      sheetData.push([
        getTranslation("REPORTS_NO_OBJECTS") || "No objects in this category.",
      ]);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 40 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
    ];

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      getTranslation("REPORTS_SHEET_TAB") || "Site meeting",
    );
    const workbookArray = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    return new Blob([workbookArray], { type: EXCEL_MIME });
  };

  const handleGenerateReport = async () => {
    if (!selectedContract || !previousMeetingDate || !currentMeetingDate) {
      setGenerationError(
        getTranslation("REPORTS_GENERATE_VALIDATION") ||
          "Select a contract and both meeting dates before creating the report.",
      );
      return;
    }
    setGenerationError(null);
    setGenerating(true);
    try {
      // Call API to fetch object data
      const reportData = await generateSiteMeetingReport({
        addressSystemId: selectedAddressSystemId,
        currentUserId,
        contractId: Number(selectedContractId),
        previousMeetingDate: dayjs(previousMeetingDate).format("YYYY-MM-DD"),
        currentMeetingDate: dayjs(currentMeetingDate).format("YYYY-MM-DD"),
      });

      const prevDateText = formatDate(previousMeetingDate);
      const currDateText = formatDate(currentMeetingDate);
      
      // Build Excel with actual data from API
      const blob = buildWorkbook(
        selectedContract.name,
        prevDateText,
        currDateText,
        reportData.approvedObjects,
        reportData.approvedHeldObjects,
      );
      
      const filename = `site-meeting-${sanitizeFileSegment(selectedContract.name)}-${dayjs(currentMeetingDate).format("YYYYMMDD")}.xlsx`;
      saveAs(blob, filename);
      setGeneratedReport({ blob, filename });
    } catch (error) {
      console.error("Failed to generate site meeting report", error);
      setGenerationError(
        getTranslation("REPORTS_GENERATE_ERROR") ||
          "Failed to generate the report. Please try again.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadAgain = () => {
    if (!generatedReport) return;
    saveAs(generatedReport.blob, generatedReport.filename);
  };

  const contractSelector = (() => {
    if (contractsLoading) {
      return (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {getTranslation("REPORTS_LOADING_CONTRACTS") ||
              "Loading contracts..."}
          </Text>
        </Group>
      );
    }

    if (contracts.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          {getTranslation("REPORTS_NO_CONTRACTS") ||
            "No contracts available for this address system."}
        </Text>
      );
    }

    if (filteredContracts.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          {getTranslation("REPORTS_NO_CONTRACTS_FOR_STATUS") ||
            "No contracts available for the selected status."}
        </Text>
      );
    }

    return (
      <Radio.Group
        value={selectedContractId}
        onChange={setSelectedContractId}
        styles={{ label: { fontWeight: 500 } }}
      >
        <Stack gap={4}>
          {filteredContracts.map((contract) => (
            <Radio
              key={contract.id}
              value={String(contract.id)}
              label={contract.name}
            />
          ))}
        </Stack>
      </Radio.Group>
    );
  })();

  if (!checkAccess("CITY_OBJECT_MEETING_REPORT")) {
    return (
      <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
        <Alert color="yellow">
          {getTranslation("REPORTS_NO_ACCESS") ||
            "You do not have permission to view this report."}
        </Alert>
      </Card>
    );
  }

  return (
    <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
      <Stack gap="md">
        {showHeader && (
          <ReportSectionHeader
            onBack={onBack}
            contextLabel={getTranslation("REPORTS_HUB_LABEL") || "Reports"}
            title={
              getTranslation("REPORTS_SITE_MEETING_TITLE") ||
              "Site meeting report"
            }
            backLabel={getTranslation("REPORTS_RETURN") || "Back to reports"}
            leadingVisual={
              <ThemeIcon size="xl" radius="xl" color="blue" variant="light">
                <IconCalendarStats size={24} />
              </ThemeIcon>
            }
          />
        )}

        {showHeader && <Divider variant="dashed" />}

        {contractsError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {contractsError}
          </Alert>
        )}

        <Stack gap="xs">
          <Text fw={600}>
            {getTranslation("REPORTS_CONTRACT_STATUS") || "Contract Status:"}
          </Text>
          <Select
            value={contractStatus}
            onChange={(value) =>
              setContractStatus(value as "active" | "archived")
            }
            data={[
              {
                value: "active",
                label:
                  getTranslation("REPORTS_ACTIVE_CONTRACTS") ||
                  "Active Contracts",
              },
              {
                value: "archived",
                label:
                  getTranslation("REPORTS_ARCHIVED_CONTRACTS") ||
                  "Archived Contracts",
              },
            ]}
            placeholder={
              getTranslation("REPORTS_SELECT_CONTRACT_STATUS") ||
              "Select contract status"
            }
          />
        </Stack>

        <Stack gap="xs">
          <Text fw={600}>
            {getTranslation("REPORTS_CONTRACT_LABEL") || "Contract:"}
          </Text>
          {contractSelector}
        </Stack>

        <Stack gap="xs">
          <Text fw={600}>
            {getTranslation("REPORTS_PREVIOUS_MEETING") ||
              "Date of the previous meeting:"}
          </Text>
          <DateInput
            valueFormat={DATE_FORMAT}
            placeholder={DATE_FORMAT}
            value={previousMeetingDate}
            onChange={setPreviousMeetingDate}
            required
          />
        </Stack>

        <Stack gap="xs">
          <Text fw={600}>
            {getTranslation("REPORTS_CURRENT_MEETING") ||
              "Date of this meeting:"}
          </Text>
          <DateInput
            valueFormat={DATE_FORMAT}
            placeholder={DATE_FORMAT}
            value={currentMeetingDate}
            onChange={setCurrentMeetingDate}
            required
          />
        </Stack>

        {generationError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {generationError}
          </Alert>
        )}

        <Button
          onClick={handleGenerateReport}
          disabled={
            !selectedContractId || !previousMeetingDate || !currentMeetingDate
          }
          loading={generating}
        >
          {getTranslation("REPORTS_CREATE_BUTTON") || "Create a report"}
        </Button>

        {generatedReport && (
          <Alert color="blue" icon={<IconDownload size={18} />}>
            <Text size="sm">
              {getTranslation("REPORTS_DOWNLOAD_HINT") ||
                "You can download the report in XLSX format"}{" "}
              <Anchor
                component="button"
                type="button"
                c="blue"
                onClick={handleDownloadAgain}
                size="sm"
                style={{ verticalAlign: "baseline" }}
              >
                {getTranslation("REPORTS_DOWNLOAD_LINK") || "here"}
              </Anchor>
              .
            </Text>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
